import React, { useState, useEffect, useRef, useMemo } from 'react';
// @ts-ignore
import ReactPlayer from 'react-player';
import {
  Play,
  Pause,
  SkipForward,
  Plus,
  List,
  Trash2,
  Search,
  Link as LinkIcon,
  Film,
  Users,
  Shield,
  Volume2,
  VolumeX,
  RefreshCw,
  ExternalLink,
  ChevronDown,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { supabase } from '../supabase';
import ErrorBoundary from '../ErrorBoundary';

const Player = (ReactPlayer as any).default || ReactPlayer;

// Popular public Invidious instances. Each is an open-source YouTube
// frontend with ad-blocking built-in. Users can swap from the UI.
// Default to Jing Rocks (reliable, well-known public instance).
export const INVIDIOUS_INSTANCES = [
  { url: 'https://inv.zoomerville.com', name: 'Zoomerville (US)' },
  { url: 'https://invidious.f5.si', name: 'F5 (JP)' },
  { url: 'https://inv.nadeko.net', name: 'Nadeko (CL)' },
  { url: 'https://invidious.nerdvpn.de', name: 'NerdVPN (DE)' },
  { url: 'https://yt.chocolatemoo53.com', name: 'ChocolateMoo (US)' },
  { url: 'https://invidious.tiekoetter.com', name: 'Tiekoetter (DE)' }
];

// Best-effort fallback search backend: Piped is the sister project to
// Invidious (same YouTube-frontend role). Its public API endpoints
// (`/search?q=...&filter=videos`) are currently more resilient against
// YouTube's anti-bot IP-blocking than Invidious's `/api/v1/search`.
// We try Piped instances first in `handleSearch`'s fallback chain, and
// the Cloudflare Pages Function (`/api/invidious-search`) prefers them
// too. Result items are normalised to Invidious's shape via
// `normalizePipedResults` so the render client doesn't need to branch.
const PIPED_INSTANCES = [
  { url: 'https://api.piped.private.coffee', name: 'Piped (private.coffee)' },
  { url: 'https://pipedapi.kavin.rocks', name: 'Piped (kavin.rocks)' },
  { url: 'https://pipedapi.adminforge.de', name: 'Piped (adminforge.de)' },
  { url: 'https://pipedapi-libre.kavin.rocks', name: 'Piped (libre)' },
  { url: 'https://pipedapi.in.projectsegfau.lt', name: 'Piped (projectsegfau)' }
];

/**
 * Map Piped's `{items:[{url,title,uploader,uploaderName,thumbnail,duration}]}`
 * shape into the Invidious shape the component already understands. Skips
 * items we can't extract a valid videoId from. Tolerates field-name drift
 * between Piped forks (some use `uploaderName`, others just `uploader`).
 */
function normalizePipedResults(rawItems: any[]): InvidiousSearchResult[] {
  const out: InvidiousSearchResult[] = [];
  for (const it of rawItems) {
    if (!it || typeof it !== 'object') continue;
    const urlStr = typeof it.url === 'string' ? it.url : '';
    const m = urlStr.match(/[?&]v=([\w-]{11})/);
    if (!m) continue;
    const author =
      (typeof it.uploaderName === 'string' && it.uploaderName) ||
      (typeof it.uploader === 'string' && it.uploader) ||
      '';
    const thumbRaw = it.thumbnail;
    const thumbUrl = Array.isArray(thumbRaw)
      ? (typeof thumbRaw[0] === 'string' ? thumbRaw[0] : undefined)
      : (typeof thumbRaw === 'string' ? thumbRaw : undefined);
    const videoThumbnails = thumbUrl
      ? [{ url: thumbUrl, quality: 'high' }]
      : undefined;
    const lengthSeconds =
      typeof it.duration === 'number' && it.duration >= 0
        ? Math.floor(it.duration)
        : 0;
    out.push({
      type: 'video',
      title: typeof it.title === 'string' ? it.title : '',
      videoId: m[1],
      author,
      lengthSeconds,
      videoThumbnails,
    });
  }
  return out;
}

interface InvidiousSearchResult {
  type: 'video' | 'playlist' | 'channel';
  title: string;
  videoId: string;
  author: string;
  lengthSeconds: number;
  videoThumbnails?: { url: string; quality: string }[];
  webpage_url?: string;
  source?: string;
}

interface Track {
  url: string;
  title: string;
  addedBy: string;
  thumbnail?: string;
  videoId?: string;
}

interface WatchTogetherProps {
  vaultId: string;
  channelId: string;
  currentUserId: string;
  currentUsername: string;
  participants: any[];
  onHostStatusChange?: (isHost: boolean) => void;
}

/**
 * Extract the YouTube videoId (11 chars) from any common YouTube URL
 * (watch?v=, youtu.be/, /shorts/, /embed/). Returns null if not a YT URL.
 *
 * Fallback regex is gated on a `youtu` substring in the URL — otherwise a
 * plain URL containing "v=" could falsely match (e.g. /foo/v=dQw4w9WgXcQ).
 */
function extractVideoId(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      return u.pathname.slice(1).split('/')[0] || null;
    }
    if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
      if (u.pathname.startsWith('/embed/')) {
        return u.pathname.split('/')[2] || null;
      }
      if (u.pathname.startsWith('/shorts/')) {
        return u.pathname.split('/')[2] || null;
      }
      const v = u.searchParams.get('v');
      return v || null;
    }
    return null;
  } catch {
    // Not a parseable URL — only try loose match if URL clearly references YT.
    if (!/youtu(?:be)?/i.test(url)) return null;
    const m = url.match(/youtu(?:be)?(?:\.com|\.be)?[\/=]([\w-]{11})/);
    return m ? m[1] : null;
  }
}

/**
 * Convert any YouTube / Invidious URL into the canonical
 * https://youtube.com/watch?v=ID form, which react-player recognises.
 */
function normalizeToYouTube(url: string): string {
  // If it's a soundcloud URL, return as is (react-player supports it)
  if (url.includes('soundcloud.com')) return url;
  
  const id = extractVideoId(url);
  return id ? `https://www.youtube.com/watch?v=${id}` : url;
}

/**
 * Convert any YouTube / Invidious URL into the Invidious embed form
 * for the chosen instance. Used by the "Ad-free embed" playback source.
 */
function normalizeToInvidiousEmbed(url: string, instance: string): string {
  const id = extractVideoId(url);
  if (!id) return url;
  const trimmed = instance.replace(/\/+$/, '');
  return `${trimmed}/embed/${id}?autoplay=1`;
}

export default function WatchTogether({
  vaultId,
  channelId,
  currentUserId,
  currentUsername,
  participants,
  onHostStatusChange,
}: WatchTogetherProps) {
  // ---------- Sticky settings (persisted in localStorage) ----------
  const STORAGE_KEY = 'watchTogether.prefs.v1';
  const loadPrefs = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };
  const initialPrefs = loadPrefs();
  const [invidiousInstance, setInvidiousInstance] = useState<string>(
    initialPrefs.instance || INVIDIOUS_INSTANCES[0].url
  );
  const [playbackSource, setPlaybackSource] = useState<'youtube' | 'invidious'>(
    initialPrefs.source || 'youtube'
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ instance: invidiousInstance, source: playbackSource })
      );
    } catch {/* private mode */}
  }, [invidiousInstance, playbackSource]);

  // ---------- Player state ----------
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('https://www.youtube.com/watch?v=jfKfPfyJRdk');
  const [currentTitle, setCurrentTitle] = useState('Lofi Hip Hop Radio');
  const [queue, setQueue] = useState<Track[]>([]);
  const [hostId, setHostId] = useState<string>(currentUserId);
  const [accessMode, setAccessMode] = useState<'host' | 'shared'>('host');
  const [isQueueOpen, setIsQueueOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => {
    try {
      const stored = localStorage.getItem('watchTogether.volume');
      return stored ? parseFloat(stored) : 0.8;
    } catch { return 0.8; }
  });

  useEffect(() => {
    try {
      localStorage.setItem('watchTogether.volume', volume.toString());
    } catch {}
  }, [volume]);

  const playerRef = useRef<any>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const channelRef = useRef<any>(null);
  const isSyncingRef = useRef(false);
  const currentTimeRef = useRef(0);
  const lastBroadcastAtRef = useRef<number>(0);

  const isHost = hostId === currentUserId;
  const canControl = accessMode === 'shared' || isHost;

  useEffect(() => {
    onHostStatusChange?.(isHost);
  }, [isHost, onHostStatusChange]);

  // ---------- Search (Invidious API) ----------
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<InvidiousSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isBrowserOpen, setIsBrowserOpen] = useState(true);

  // Pick whichever URL to render in the player surface.
  const resolvedPlayerUrl = useMemo(() => {
    if (playbackSource === 'invidious') {
      return normalizeToInvidiousEmbed(currentUrl, invidiousInstance);
    }
    return normalizeToYouTube(currentUrl);
  }, [currentUrl, playbackSource, invidiousInstance]);

  // ---------- Realtime sync ----------
  useEffect(() => {
    const channelName = `vault_activity_watch_${vaultId}_${channelId}`;
    const channel = supabase.channel(channelName);

    channel
      .on('broadcast', { event: 'state-change' }, ({ payload }) => {
        if (payload.senderId === currentUserId) return;

        if (payload.currentUrl !== currentUrl) {
          setCurrentUrl(payload.currentUrl);
        }
        if (payload.currentTitle && payload.currentTitle !== currentTitle) {
          setCurrentTitle(payload.currentTitle);
        }

        setIsPlaying(payload.isPlaying);
        setHostId(payload.hostId);
        setAccessMode(payload.accessMode);
        setQueue(payload.queue);

        const localTime = currentTimeRef.current;
        const remoteTime = payload.currentTime;
        const drift = Math.abs(localTime - remoteTime);

        if (drift > 1.5 && !isSyncingRef.current) {
          isSyncingRef.current = true;
          try {
            playerRef.current?.seekTo?.(remoteTime, 'seconds');
          } catch {/* ignore */}
          setTimeout(() => {
            isSyncingRef.current = false;
          }, 500);
        }
      })
      .on('broadcast', { event: 'request-sync' }, ({ payload }) => {
        if (isHost && payload.requesterId !== currentUserId) {
          broadcastState(currentTimeRef.current, isPlaying);
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          channelRef.current = channel;
          channel.send({
            type: 'broadcast',
            event: 'request-sync',
            payload: { requesterId: currentUserId },
          });
        }
      });

    return () => {
      channel.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultId, channelId, currentUserId]);

  const broadcastState = (
    time: number,
    playingState: boolean,
    overrideUrl?: string,
    overrideQueue?: Track[]
  ) => {
    if (!channelRef.current || !canControl) return;
    channelRef.current.send({
      type: 'broadcast',
      event: 'state-change',
      payload: {
        senderId: currentUserId,
        currentUrl: overrideUrl !== undefined ? overrideUrl : currentUrl,
        currentTitle,
        isPlaying: playingState,
        currentTime: time,
        hostId,
        accessMode,
        queue: overrideQueue || queue,
      },
    });
  };

  // ---------- Periodic state broadcasts from the host ----------
  useEffect(() => {
    if (!isHost || !isPlaying) return;

    const interval = setInterval(() => {
      const now = Date.now();
      // Throttle: max once per 2s.
      if (now - lastBroadcastAtRef.current < 1900) return;
      lastBroadcastAtRef.current = now;
      broadcastState(currentTimeRef.current, isPlaying);
    }, 2000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHost, isPlaying]);

  // ---------- Search (YouTube via Invidious API) ----------
  // AbortController + monotonic request id guard against out-of-order
  // responses when the user fires multiple searches in rapid succession.
  const searchAbortRef = useRef<AbortController | null>(null);
  const searchRequestIdRef = useRef<number>(0);

  const resolveThumbUrl = (raw: string | undefined): string | undefined => {
    if (!raw) return undefined;
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('//')) {
      // Protocol-relative paths: prepend current origin's protocol.
      if (raw.startsWith('//')) return `https:${raw}`;
      return raw;
    }
    const trimmed = invidiousInstance.replace(/\/+$/, '');
    return `${trimmed}${raw.startsWith('/') ? '' : '/'}${raw}`;
  };

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;

    // Cancel any in-flight search so stale responses don't overwrite newer ones.
    if (searchAbortRef.current) searchAbortRef.current.abort();
    const ctrl = new AbortController();
    searchAbortRef.current = ctrl;
    const requestId = ++searchRequestIdRef.current;

    setIsSearching(true);
    setSearchError(null);
    setSearchResults([]);

    const isStale = () => requestId !== searchRequestIdRef.current;

    // ---- Production path: proxy search through our Cloudflare Pages Function or new Backend API ----
    // We added a new backend API at `/api/search-media` which uses yt-dlp to search both YouTube and SoundCloud.
    // If it fails, we fall back to Invidious logic.
    try {
      const apiUrl = `/api/search-media?q=${encodeURIComponent(q)}`;
      const apiRes = await fetch(apiUrl, {
        signal: AbortSignal.timeout(15000), 
        headers: { Accept: 'application/json' },
      });
      if (apiRes.ok) {
        const data = (await apiRes.json()) as any;
        if (!isStale() && !ctrl.signal.aborted) {
          if (data.results && data.results.length > 0) {
            setSearchResults(data.results);
            setIsSearching(false);
            return;
          }
          if (data.error) {
            setSearchError(data.error);
          }
        }
      }
    } catch {
      // fetch threw (local dev, network offline, etc.) → fall through
    }
    
    if (isStale() || ctrl.signal.aborted) return;

    // ---- Client-side fallback (local dev only — production uses the
    //      /api/invidious-search Pages Function, which prefers Piped).
    //
    // Shared two-step fetcher used by both Invidious and Piped probes:
    //   1. Direct fetch — succeeds when the upstream sends CORS headers
    //      (faster path, no third-party hop).
    //   2. corsproxy.io fallback — bypasses CORS for any target.
    //
    // CRITICAL: each attempt uses its OWN local AbortController for the
    // per-attempt timeout. Sharing the outer `ctrl` (used for user-cancel)
    // would mean a per-attempt timeout poison ctrl.signal.aborted and the
    // outer catch would skip the next attempt — defeating the whole point
    // of the two-step pattern. The outer signal propagates to each local
    // one via addEventListener so user-cancel still cascades down. The
    // synchronous `if (ctrl.signal.aborted) onOuter();` line closes the
    // race between the would-be check and the listener attachment.
    const tryFetch = async (url: string, timeoutMs: number): Promise<unknown> => {
      const local = new AbortController();
      const timer = setTimeout(() => local.abort(), timeoutMs);
      let onOuter: (() => void) | null = () => local.abort();
      ctrl.signal.addEventListener('abort', onOuter);
      if (ctrl.signal.aborted) onOuter();
      try {
        const res = await fetch(url, {
          signal: local.signal,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.json();
      } finally {
        clearTimeout(timer);
        if (onOuter) ctrl.signal.removeEventListener('abort', onOuter);
      }
    };

    const tryInstance = async (instanceUrl: string): Promise<InvidiousSearchResult[]> => {
      const trimmed = instanceUrl.replace(/\/+$/, '');
      const targetUrl = `${trimmed}/api/v1/search?q=${encodeURIComponent(q)}&type=video&fields=title,videoId,author,lengthSeconds,videoThumbnails`;
      try {
        const json = await tryFetch(targetUrl, 4000);
        return Array.isArray(json) ? (json as InvidiousSearchResult[]) : [];
      } catch (directErr) {
        // Only skip the proxy attempt when the USER actually cancelled
        // (not when our 4s direct timeout fired — that wouldn't have set
        // ctrl.signal.aborted since the timeout uses a local controller).
        if (ctrl.signal.aborted) throw directErr;
        // CORS rejection, network error, or HTTP failure → try the proxy.
        const json = await tryFetch(
          `https://corsproxy.io/?${encodeURIComponent(targetUrl)}`,
          8000
        );
        return Array.isArray(json) ? (json as InvidiousSearchResult[]) : [];
      }
    };

    // Piped sister project — more resilient against current YouTube's
    // anti-bot IP-blocking. Hit `/search?q=...&filter=videos` and
    // normalise the response shape to Invidious's so the render path
    // doesn't need to branch.
    const tryPipedInstance = async (
      instanceUrl: string
    ): Promise<InvidiousSearchResult[]> => {
      const trimmed = instanceUrl.replace(/\/+$/, '');
      const targetUrl = `${trimmed}/search?q=${encodeURIComponent(q)}&filter=videos`;
      const fetchJson = async () => {
        try {
          return await tryFetch(targetUrl, 4000);
        } catch (directErr) {
          if (ctrl.signal.aborted) throw directErr;
          return await tryFetch(
            `https://corsproxy.io/?${encodeURIComponent(targetUrl)}`,
            8000
          );
        }
      };
      const json = await fetchJson();
      const items =
        json &&
        typeof json === 'object' &&
        Array.isArray((json as { items?: unknown[] }).items)
          ? (json as { items: any[] }).items
          : [];
      return normalizePipedResults(items);
    };

    try {
      const results = await tryInstance(invidiousInstance);
      if (isStale() || ctrl.signal.aborted) return;
      setSearchResults(results.filter((r) => r.type === 'video'));
    } catch (err) {
      if (isStale() || ctrl.signal.aborted) return;
      console.warn('Invidious search failed on', invidiousInstance, err);

      // Piped first — its public `/search?q=...&filter=videos` API is
      // currently the most reliable YouTube-search backend reachable from
      // residential + datacenter networks alike. Each response is
      // normalised to Invidious-shape inside `tryPipedInstance` so the
      // render path stays branchless.
      let recovered = false;
      for (const inst of PIPED_INSTANCES) {
        try {
          const results = await tryPipedInstance(inst.url);
          if (isStale() || ctrl.signal.aborted) return;
          if (results.length === 0) continue;
          setSearchResults(results);
          setSearchError(`Search fell back to ${inst.name} (Piped)`);
          recovered = true;
          break;
        } catch {/* keep trying */}
      }

      // Last-ditch fallback: try every other Invidious instance once.
      if (!recovered) {
        for (const inst of INVIDIOUS_INSTANCES) {
          if (inst.url === invidiousInstance) continue;
          try {
            const results = await tryInstance(inst.url);
            if (isStale() || ctrl.signal.aborted) return;
            setSearchResults(results.filter((r) => r.type === 'video'));
            setSearchError(`Primary instance unreachable — fell back to ${inst.name}`);
            setInvidiousInstance(inst.url);
            recovered = true;
            break;
          } catch {/* keep trying */}
        }
      }

      if (!recovered && !isStale()) {
        setSearchError(
          'All Invidious and Piped instances are unreachable. Paste a YouTube URL below to play manually.'
        );
      }
    } finally {
      if (!isStale()) setIsSearching(false);
    }
  };

  // Resume playback when returning from games or other activities.
  // When WatchTogether is hidden (e.g. behind an active game), the browser
  // may pause the player. When the user comes back, resume if it was playing.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isPlaying && playerRef.current) {
        // Ensure player resumes if it was paused while hidden
        playerRef.current?.seekTo?.(currentTimeRef.current, 'seconds');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isPlaying]);

  // ---------- Playback controls ----------
  const handlePlayPause = () => {
    if (!canControl) return;
    const nextPlaying = !isPlaying;
    setIsPlaying(nextPlaying);
    broadcastState(currentTimeRef.current, nextPlaying);
  };

  // react-player onPlay/onPause fire when the underlying element emits the
  // native event — e.g. when a video auto-resumes after the host rejoins.
  // Mirror those into Supabase so peers don't drift from the host's intent.
  const handleNativePlay = () => {
    if (!isPlaying) {
      setIsPlaying(true);
      if (canControl) broadcastState(currentTimeRef.current, true);
    }
  };
  const handleNativePause = () => {
    if (isPlaying) {
      setIsPlaying(false);
      if (canControl) broadcastState(currentTimeRef.current, false);
    }
  };

  const handleProgress = (state: { playedSeconds: number }) => {
    setCurrentTime(state.playedSeconds);
    currentTimeRef.current = state.playedSeconds;
  };

  const handleSeek = (seconds: number) => {
    if (!canControl) return;
    try {
      playerRef.current?.seekTo?.(seconds, 'seconds');
    } catch {/* ignore */}
    broadcastState(seconds, isPlaying);
  };

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${min}:${sec < 10 ? '0' : ''}${sec}`;
  };

  // Queue ops: both URL paste and search-result click feed into the same
  // unified queue logic.
  const enqueueOrPlay = (input: { url: string; title: string; thumbnail?: string; videoId?: string }) => {
    const normalized = normalizeToYouTube(input.url);

    if (!currentUrl) {
      setCurrentUrl(normalized);
      setCurrentTitle(input.title);
      setIsPlaying(true);
      setTimeout(() => broadcastState(0, true, normalized, []), 500);
      return;
    }

    const newTrack: Track = {
      url: normalized,
      title: input.title,
      addedBy: currentUsername,
      thumbnail: input.thumbnail,
      videoId: input.videoId || extractVideoId(normalized) || undefined,
    };
    const updated = [...queue, newTrack];
    setQueue(updated);
    broadcastState(currentTimeRef.current, isPlaying, currentUrl, updated);
  };

  const removeFromQueue = (index: number) => {
    if (!canControl) return;
    const updated = queue.filter((_, i) => i !== index);
    setQueue(updated);
    broadcastState(currentTimeRef.current, isPlaying, currentUrl, updated);
  };

  const playNext = () => {
    if (!canControl || queue.length === 0) return;
    const nextTrack = queue[0];
    const updated = queue.slice(1);
    setCurrentUrl(nextTrack.url);
    setCurrentTitle(nextTrack.title);
    setQueue(updated);
    setIsPlaying(true);
    setTimeout(() => broadcastState(0, true, nextTrack.url, updated), 500);
  };

  const toggleAccessMode = () => {
    if (!isHost) return;
    const nextMode = accessMode === 'host' ? 'shared' : 'host';
    setAccessMode(nextMode);
    setTimeout(() => broadcastState(currentTimeRef.current, isPlaying), 200);
  };

  const claimHost = () => {
    setHostId(currentUserId);
    setTimeout(() => broadcastState(currentTimeRef.current, isPlaying), 200);
  };

  const passHost = (newHostId: string) => {
    setHostId(newHostId);
    setTimeout(() => broadcastState(currentTimeRef.current, isPlaying), 200);
  };

  const swapInvidiousInstance = (url: string) => {
    setInvidiousInstance(url);
    setSearchResults([]);
    setSearchError(null);
  };

  return (
    <div className="w-full h-full flex flex-col md:flex-row bg-black/80 rounded-2xl border-2 border-[var(--neon)] shadow-[0_0_25px_var(--neon-glow)] overflow-hidden">
      {/* Main column */}
      <div className="flex-1 flex flex-col relative min-h-0 bg-slate-950">
        {/* Top bar */}
        <div className="flex items-center justify-between p-3 border-b border-white/5 bg-black/40 z-10 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Film className="w-4 h-4 text-[var(--neon)]" />
            <span className="text-[10px] font-black uppercase tracking-widest text-white">Watch Together</span>
            <span className="hidden md:inline text-[9px] text-white/30 font-mono normal-case truncate max-w-[180px]">
              · {currentTitle}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Source toggle */}
            <button
              onClick={() => setPlaybackSource((s) => (s === 'youtube' ? 'invidious' : 'youtube'))}
              className="flex items-center gap-1.5 px-2.5 py-1 text-[9px] font-black uppercase border border-[var(--neon)]/25 hover:border-[var(--neon)] text-[var(--neon)] rounded-lg transition-all"
              title={
                playbackSource === 'youtube'
                  ? 'Playing via YouTube (sync-optimized). Click to switch to ad-free Invidious.'
                  : 'Playing via Invidious embed (ad-blocked). Click to switch to YouTube.'
              }
            >
              {playbackSource === 'youtube' ? 'YT' : 'INV'}
              <span>{playbackSource === 'youtube' ? 'sync' : 'ad-free'}</span>
            </button>

            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold text-white/60">
                Host: {isHost ? 'You' : participants?.find((p) => p.id === hostId)?.name || 'Peer'}
              </span>

              {isHost ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleAccessMode}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-[9px] font-black uppercase border border-[var(--neon)]/25 hover:border-[var(--neon)] text-[var(--neon)] rounded-lg transition-all"
                    title="Toggle who can control playback"
                  >
                    <Shield size={10} />
                    <span>Mode: {accessMode}</span>
                  </button>
                  <details className="relative">
                    <summary className="list-none cursor-pointer text-[9px] font-mono uppercase border border-[var(--neon)]/25 px-2 py-1 rounded-lg text-white/60 hover:text-[var(--neon)] hover:border-[var(--neon)] flex items-center gap-1 transition-all select-none">
                      Pass Host <ChevronDown size={9} />
                    </summary>
                    <div className="absolute right-0 top-full mt-1 bg-black/95 border border-[var(--neon)]/30 rounded-lg shadow-[0_0_15px_rgba(0,255,255,0.1)] z-30 min-w-[120px] py-1">
                      {participants?.filter((p) => p.id !== currentUserId).length ? (
                        participants.filter((p) => p.id !== currentUserId).map((p) => (
                          <button
                            key={p.id}
                            onClick={(e) => {
                              passHost(p.id);
                              (e.target as HTMLElement).closest('details')?.removeAttribute('open');
                            }}
                            className="w-full text-left px-3 py-1.5 text-[10px] hover:bg-white/10 text-white/80 transition-colors"
                          >
                            {p.name}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-1.5 text-[9px] text-white/30 italic">No peers</div>
                      )}
                    </div>
                  </details>
                </div>
              ) : (
                <button
                  onClick={claimHost}
                  className="px-2 py-1 text-[9px] font-bold border border-white/20 rounded hover:border-[var(--neon)] text-[var(--neon)] transition-all"
                  title="Forcefully take host"
                >
                  Claim
                </button>
              )}
            </div>

            <button
              onClick={() => setIsBrowserOpen((v) => !v)}
              className={`p-1.5 rounded-lg transition-all ${
                isBrowserOpen
                  ? 'bg-[var(--neon)]/10 text-[var(--neon)] border border-[var(--neon)]/40'
                  : 'bg-white/5 text-white/60 border border-white/10 hover:text-[var(--neon)]'
              }`}
              title="Toggle YouTube browser"
            >
              <Search size={13} />
            </button>

            <button
              onClick={() => setIsQueueOpen((v) => !v)}
              className={`p-1.5 rounded-lg transition-all ${
                isQueueOpen
                  ? 'bg-[var(--neon)]/10 text-[var(--neon)] border border-[var(--neon)]/40'
                  : 'bg-white/5 text-white/60 border border-white/10 hover:text-[var(--neon)]'
              }`}
              title="Toggle queue"
            >
              <List size={13} />
            </button>
          </div>
        </div>

        {/* Player stage */}
        <div className="flex-1 min-h-0 relative flex items-center justify-center bg-black">
          {playbackSource === 'youtube' ? (
            <ErrorBoundary>
              <Player
                ref={playerRef}
                key={currentUrl}
                url={currentUrl}
                playing={isPlaying}
                volume={volume}
                onProgress={handleProgress as any}
                onEnded={playNext}
                onDuration={(d: number) => setDuration(d)}
                onPlay={handleNativePlay}
                onPause={handleNativePause}
                width="100%"
                height="100%"
                style={{ position: 'absolute', top: 0, left: 0 }}
                config={
                  {
                    youtube: {
                      playerVars: {
                        controls: 0,
                        rel: 0,
                        modestbranding: 1,
                        // Helps prevent some inline ads from rendering.
                        iv_load_policy: 3,
                      },
                    },
                  } as any
                }
              />
            </ErrorBoundary>
          ) : (
            // Invidious iframe embed (no ads by design, but no sync)
            <iframe
              ref={iframeRef}
              key={resolvedPlayerUrl}
              src={resolvedPlayerUrl}
              title={currentTitle}
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          )}

          {/* When in YouTube mode we overlay our own seekable scrubber.
              Iframe embed mode hides the scrubber since Invidious doesn't
              expose a synchronous seek API. */}
          {playbackSource === 'youtube' && (
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/95 via-black/60 to-transparent pointer-events-none">
              <div className="pointer-events-auto flex items-center gap-3">
                <button
                  onClick={handlePlayPause}
                  disabled={!canControl}
                  className={`p-2 rounded-xl transition-all ${
                    canControl ? 'text-[var(--neon)] hover:bg-white/10' : 'text-white/20'
                  }`}
                >
                  {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                </button>

                {queue.length > 0 && (
                  <button
                    onClick={playNext}
                    disabled={!canControl}
                    className={`p-2 rounded-xl transition-all ${
                      canControl ? 'text-[var(--neon)] hover:bg-white/10' : 'text-white/20'
                    }`}
                    title="Play Next"
                  >
                    <SkipForward size={18} />
                  </button>
                )}

                <div className="flex items-center gap-2 mx-2">
                  <button
                    onClick={() => setVolume(v => v === 0 ? 0.8 : 0)}
                    className="text-white/60 hover:text-white transition-colors p-1"
                  >
                    {volume === 0 ? <VolumeX size={14} /> : <Volume2 size={14} />}
                  </button>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    className="w-16 h-1 bg-white/10 rounded-full appearance-none cursor-pointer accent-[var(--neon)]"
                    title="Volume"
                  />
                </div>

                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <span className="text-[10px] text-white/60 font-mono shrink-0">
                    {formatTime(currentTime)}
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={duration || 100}
                    value={currentTime}
                    disabled={!canControl}
                    onChange={(e) => handleSeek(parseFloat(e.target.value))}
                    className="flex-1 h-1 bg-white/10 rounded-full appearance-none cursor-pointer accent-[var(--neon)]"
                  />
                  <span className="text-[10px] text-white/60 font-mono shrink-0">
                    {formatTime(duration)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {playbackSource === 'invidious' && (
            <div className="absolute top-3 left-3 bg-black/70 backdrop-blur px-3 py-1.5 rounded-lg border border-white/10 text-[10px] text-white/60 flex items-center gap-2 z-10">
              <CheckCircle2 size={12} className="text-emerald-400" />
              <span>Ad-free via Invidious (sync disabled)</span>
            </div>
          )}
        </div>
      </div>

      {/* Side panel: search + queue */}
      {(isBrowserOpen || isQueueOpen) && (
        <div className="w-full md:w-80 bg-slate-900/90 border-l border-white/5 flex flex-col min-h-[200px] max-h-full overflow-hidden">
          {/* Search panel */}
          {isBrowserOpen && (
            <div className={`flex flex-col gap-3 p-3 border-b border-white/5 ${isQueueOpen ? 'max-h-[60%]' : 'flex-1 min-h-0'}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-black uppercase tracking-widest text-white/60 flex items-center gap-2">
                  <Search size={11} className="text-[var(--neon)]" />
                  Browse YouTube & SoundCloud
                </div>

                {/* Instance selector */}
                <details className="relative">
                  <summary className="list-none cursor-pointer text-[9px] font-mono text-white/40 hover:text-[var(--neon)] flex items-center gap-1 select-none">
                    <span className="truncate max-w-[120px]">
                      {INVIDIOUS_INSTANCES.find((i) => i.url === invidiousInstance)?.name ||
                        new URL(invidiousInstance).hostname}
                    </span>
                    <ChevronDown size={9} />
                  </summary>
                  <div className="absolute right-0 top-full mt-1 bg-black/95 border border-white/10 rounded-lg shadow-xl z-30 min-w-[180px] py-1 max-h-[260px] overflow-y-auto no-scrollbar">
                    {INVIDIOUS_INSTANCES.map((inst) => (
                      <button
                        key={inst.url}
                        onClick={(e) => {
                          e.preventDefault();
                          swapInvidiousInstance(inst.url);
                          (e.currentTarget.closest('details') as HTMLDetailsElement | null)?.removeAttribute('open');
                        }}
                        className={`w-full text-left px-3 py-1.5 text-[10px] hover:bg-white/5 flex items-center justify-between gap-2 ${
                          invidiousInstance === inst.url ? 'text-[var(--neon)]' : 'text-white/70'
                        }`}
                      >
                        <span className="truncate">{inst.name}</span>
                        {invidiousInstance === inst.url && <CheckCircle2 size={10} />}
                      </button>
                    ))}
                  </div>
                </details>
              </div>

              <form onSubmit={handleSearch} className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search videos…"
                  className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/5 text-xs text-white focus:outline-none focus:border-[var(--neon)]/50"
                />
                <button
                  type="submit"
                  disabled={isSearching}
                  className="px-3 bg-[var(--neon)] text-black rounded-lg text-xs font-bold hover:bg-white transition-all disabled:opacity-50"
                  title="Search"
                >
                  {isSearching ? <RefreshCw size={12} className="animate-spin" /> : <Search size={12} />}
                </button>
              </form>

              {searchError && (
                <div className="text-[10px] text-amber-300/80 flex items-start gap-1.5 px-1">
                  <AlertTriangle size={10} className="shrink-0 mt-0.5" />
                  <span>{searchError}</span>
                </div>
              )}

              <div className="flex-1 overflow-y-auto flex flex-col gap-1.5 no-scrollbar pr-1">
                {isSearching && (
                  <div className="py-6 text-center text-[10px] text-white/30 font-mono uppercase animate-pulse">
                    Scanning signals…
                  </div>
                )}
                {!isSearching &&
                  searchResults.map((r) => {
                    const thumb =
                      r.videoThumbnails?.[0]?.url ||
                      r.videoThumbnails?.[1]?.url ||
                      r.videoThumbnails?.[2]?.url ||
                      undefined;
                    const ytUrl = r.webpage_url || `https://www.youtube.com/watch?v=${r.videoId}`;
                    return (
                      <button
                        key={r.videoId || ytUrl}
                        onClick={() =>
                          enqueueOrPlay({
                            url: ytUrl,
                            title: r.title,
                            thumbnail: thumb,
                            videoId: r.videoId,
                          })
                        }
                        className="flex items-center gap-2 p-2 bg-white/5 hover:bg-white/10 rounded-lg border border-white/5 text-left transition-all group"
                      >
                        {thumb ? (
                          <img
                            src={resolveThumbUrl(thumb)}
                            alt=""
                            className="w-16 h-9 object-cover rounded border border-white/10 shrink-0"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-16 h-9 rounded bg-black/50 border border-white/10 shrink-0 flex items-center justify-center">
                            <Film size={12} className="text-white/30" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-bold text-white truncate group-hover:text-[var(--neon)] flex items-center gap-1.5">
                            {r.title}
                            {r.source === "soundcloud" && <span className="px-1 py-0.5 rounded text-[8px] bg-orange-500/20 text-orange-400">SoundCloud</span>}
                          </div>
                          <div className="text-[8px] text-white/40 truncate flex items-center gap-1.5">
                            <span>{r.author}</span>
                            {r.lengthSeconds > 0 && (
                              <>
                                <span>·</span>
                                <span>{formatTime(r.lengthSeconds)}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <Plus
                          size={14}
                          className="text-[var(--neon)] opacity-0 group-hover:opacity-100 transition-all shrink-0"
                        />
                      </button>
                    );
                  })}
                {!isSearching && searchResults.length === 0 && !searchError && (
                  <div className="py-6 text-center text-[11px] text-white/30 font-mono uppercase tracking-wider max-w-[260px] mx-auto leading-relaxed">
                    Search above to queue up videos or music.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Queue panel */}
          {isQueueOpen && (
            <div className="flex-1 flex flex-col gap-2 p-3 overflow-hidden">
              <div className="text-[10px] font-black uppercase tracking-widest text-white/60 flex justify-between items-center border-b border-white/5 pb-2">
                <span className="flex items-center gap-2">
                  <List size={11} className="text-[var(--neon)]" />
                  Queue ({queue.length})
                </span>
              </div>

              <div className="flex-1 overflow-y-auto flex flex-col gap-2 no-scrollbar">
                {queue.length === 0 ? (
                  <div className="py-8 text-center text-[10px] text-white/30 font-bold uppercase tracking-wider">
                    Empty
                  </div>
                ) : (
                  queue.map((track, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 p-2 bg-white/5 rounded-xl border border-white/5 group"
                    >
                      {track.thumbnail ? (
                        <img
                          src={track.thumbnail}
                          alt=""
                          className="w-10 h-7 object-cover rounded border border-white/10 shrink-0"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-10 h-7 rounded bg-black/50 border border-white/10 shrink-0 flex items-center justify-center">
                          <Film size={10} className="text-white/30" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold text-white truncate">{track.title}</div>
                        <div className="text-[8px] text-white/30 truncate">by {track.addedBy}</div>
                      </div>
                      {canControl && (
                        <button
                          onClick={() => removeFromQueue(i)}
                          className="p-1 text-white/20 group-hover:text-red-400 hover:bg-white/5 rounded transition-all"
                          title="Remove"
                        >
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
